// Cloud account fields per provider (spec 4.7, owner: 07 Cloud accounts and secrets).
import { strings } from '../strings';

export type AccountFieldKind = 'text' | 'number' | 'json' | 'secretName' | 'secretStore';

export interface AccountField {
  key: string;
  kind: AccountFieldKind;
  required: boolean;
  help?: string;
  pattern?: RegExp;
  min?: number;
  max?: number;
}

export interface ProviderAccountFields {
  scope: AccountField[];
  methods: Record<string, AccountField[]>;
}

const GUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const help = strings.cloudAccountHelp;

const text = (key: string, required = true, extra: Partial<AccountField> = {}): AccountField => ({
  key,
  kind: 'text',
  required,
  ...extra,
});
const secretName = (key: string): AccountField => ({ key, kind: 'secretName', required: true });
const secretStore: AccountField = { key: 'secretStore', kind: 'secretStore', required: false };
const tokenFile = text('tokenFile', true, { help: help.tokenFile });

export const ACCOUNT_FIELDS: Record<string, ProviderAccountFields> = {
  aws: {
    scope: [text('accountId', true, { pattern: /^\d{12}$/, help: help.accountId })],
    methods: {
      ambient: [],
      'assume-role': [
        text('roleArn'),
        text('externalId', false),
        {
          key: 'sessionDurationMinutes',
          kind: 'number',
          required: false,
          min: 15,
          max: 60,
          help: help.sessionDurationMinutes,
        },
      ],
      federated: [text('roleArn'), tokenFile],
      'access-key': [secretName('accessKeyIdSecret'), secretName('secretAccessKeySecret'), secretStore],
    },
  },
  azure: {
    scope: [
      text('tenantId', true, { pattern: GUID, help: help.guid }),
      text('subscriptionId', true, { pattern: GUID, help: help.guid }),
    ],
    methods: {
      ambient: [],
      'managed-identity': [text('clientId', false, { help: help.managedIdentityClientId })],
      federated: [text('clientId'), tokenFile],
      'client-secret': [text('clientId'), secretName('clientSecretSecret'), secretStore],
    },
  },
  gcp: {
    scope: [text('projectId')],
    methods: {
      ambient: [],
      impersonate: [text('serviceAccountEmail')],
      federated: [{ key: 'credentialConfig', kind: 'json', required: true, help: help.credentialConfig }],
      'service-account-key': [secretName('keySecret'), secretStore],
    },
  },
  // Feature spec 17, phase 8 (spec 07 5.1): the account ID and the API token's secret name; the token itself is never
  // typed here and never shown.
  cloudflare: {
    scope: [text('accountId', true, { pattern: /^[0-9a-f]{32}$/, help: help.cloudflareAccountId })],
    methods: {
      'api-token': [
        { key: 'apiTokenSecret', kind: 'secretName', required: true, help: help.apiTokenSecret },
        secretStore,
      ],
    },
  },
};

export function fieldLabel(field: AccountField): string {
  return strings.cloudAccountFields[field.key] ?? field.key;
}

/** Long-lived key methods, accepted only when the server allows static keys. */
export const DISCOURAGED_METHODS = new Set(['access-key', 'client-secret', 'service-account-key']);
